import { useParams, Link } from "react-router-dom";
import { products, ProductVariant } from "../data/products";
import { useCart } from "../Context/CartContext";
import { usePincode } from "../Context/PincodeContext";
import { useRecentlyViewed } from "../Context/RecentlyViewedContext";
import { useState, useEffect } from "react";

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const { addToCart } = useCart();
  const { pincode, setPincode } = usePincode();
  const { addViewedProduct } = useRecentlyViewed();
  
  const product = products.find((p) => p.id === id);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(
    product?.variants?.[0] || null
  );

  useEffect(() => {
    if (product) {
      addViewedProduct(product.id);
    }
  }, [product, addViewedProduct]);
  
  // Use Product's images array if available, otherwise just use the single imageUrl
  const availableImages = product?.images && product.images.length > 0 
    ? product.images 
    : (product ? [product.imageUrl] : []);
    
  const [activeImage, setActiveImage] = useState<string>(availableImages[0] || "");

  if (!product) {
    return (
      <div className="container">
        <h2>Product not found</h2>
        <Link to="/shop">Back to Shop</Link>
      </div>
    );
  }

  const currentPrice = selectedVariant ? selectedVariant.price : product.price;
  const currentOriginalPrice = selectedVariant ? selectedVariant.originalPrice : product.originalPrice;
  const discount = currentOriginalPrice 
    ? Math.round(((currentOriginalPrice - currentPrice) / currentOriginalPrice) * 100)
    : 0;

  const handleAddToCart = () => {
    addToCart({
      id: selectedVariant ? `${product.id}-${selectedVariant.id}` : product.id,
      name: selectedVariant ? `${product.name} - ${selectedVariant.name}` : product.name,
      price: currentPrice.toString(),
      quantity: 1
    });
    alert("Added to Cart!");
  };

  const handlePincodeClick = () => {
    const code = prompt("Enter your 6-digit Pincode to see estimated delivery dates:");
    if (code && code.trim().length >= 4) {
      setPincode(code.trim());
    }
  }

  // Dummy delivery date logic
  const deliveryDate = new Date();
  deliveryDate.setDate(deliveryDate.getDate() + 4);
  const deliveryDateString = deliveryDate.toLocaleDateString("en-IN", { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div className="container checkout-grid">
      <div className="card" style={{ padding: '24px' }}>
        <img src={activeImage} alt={product.name} style={{ width: '100%', marginBottom: '16px' }} />
        
        {/* Render multiple images if available */}
        {availableImages.length > 1 && (
          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' }}>
            {availableImages.map((imgUrl, index) => (
              <img 
                key={index}
                src={imgUrl} 
                alt={`${product.name} thumbnail ${index + 1}`} 
                onClick={() => setActiveImage(imgUrl)}
                style={{ 
                  width: '60px', 
                  height: '60px', 
                  objectFit: 'cover', 
                  cursor: 'pointer',
                  border: activeImage === imgUrl ? '2px solid var(--link)' : '1px solid var(--border)',
                  borderRadius: '4px'
                }} 
              />
            ))}
          </div>
        )}
      </div>
      
      <div>
        <h1 style={{ margin: '0 0 16px 0' }}>{product.name}</h1>
        <div style={{ marginBottom: '16px' }}>
          <span style={{ background: '#388e3c', color: 'white', padding: '4px 8px', borderRadius: '4px', marginRight: '8px', fontWeight: 'bold' }}>
            {product.rating} ★
          </span>
          <span style={{ color: 'var(--text-muted)' }}>({product.reviewsCount} reviews)</span>
        </div>
        
        <div style={{ marginBottom: '24px', fontSize: '24px', fontWeight: 'bold' }}>
          ₹{currentPrice}
          {currentOriginalPrice && (
            <span style={{ marginLeft: '12px', fontSize: '18px', fontWeight: 'normal' }}>
              <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)' }}>₹{currentOriginalPrice}</span>
              <span style={{ color: '#388e3c', marginLeft: '12px' }}>{discount}% off</span>
            </span>
          )}
        </div>

        {product.variants && product.variants.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>Select Variant:</p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {product.variants.map(v => (
                <button 
                  key={v.id}
                  onClick={() => setSelectedVariant(v)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 'var(--radius-sm)',
                    border: `1px solid ${selectedVariant?.id === v.id ? 'var(--link)' : 'var(--border)'}`,
                    background: selectedVariant?.id === v.id ? '#f2ede6' : 'white',
                    cursor: 'pointer'
                  }}
                >
                  {v.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Delivery Box */}
        <div style={{ marginBottom: '24px', padding: '16px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--surface)' }}>
          <p style={{ margin: '0 0 8px 0', display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '18px', marginRight: '8px' }}>🚚</span>
            {pincode ? (
              <span>Delivery to <strong>{pincode}</strong> by <strong>{deliveryDateString}</strong></span>
            ) : (
              <span>Delivery date checking...</span>
            )}
          </p>
          {!pincode ? (
            <span style={{ color: 'var(--link)', cursor: 'pointer', fontSize: '14px', textDecoration: 'underline' }} onClick={handlePincodeClick}>
              Enter Pincode to check delivery date
            </span>
          ) : (
            <span style={{ color: 'var(--link)', cursor: 'pointer', fontSize: '14px', textDecoration: 'underline' }} onClick={handlePincodeClick}>
              Change Pincode
            </span>
          )}
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h3>Description:</h3>
          <p>{product.description}</p>
        </div>

        {product.features && product.features.length > 0 && (
          <ul style={{ paddingLeft: '20px', marginBottom: '24px' }}>
            {product.features.map((f, i) => <li key={i} style={{ marginBottom: '8px' }}>{f}</li>)}
          </ul>
        )}

        {/* FOMO Stock Trigger */}
        {(!selectedVariant || selectedVariant.stock < 50) && (
          <div style={{ color: '#d00000', fontWeight: 'bold', marginBottom: '16px' }}>
            Hurry! {selectedVariant ? `Only ${selectedVariant.stock}` : 'Few'} left in stock.
          </div>
        )}

        <div style={{ display: 'flex', gap: '16px' }}>
          <button className="btn-primary" style={{ flex: 1 }} onClick={handleAddToCart}>ADD TO CART</button>
        </div>
      </div>
      
      {/* Frequently Bought Together */}
      <div style={{ gridColumn: '1 / -1', marginTop: '28px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
        <h2>Frequently Bought Together</h2>
        <div className="card" style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center' }}>
           <div style={{ flex: 1, minWidth: '200px' }}>
             <img src={product.imageUrl} alt={product.name} style={{ width: '100px', height: '100px', objectFit: 'contain' }}/>
             <p style={{ fontWeight: 'bold', margin: '8px 0 0 0' }}>This item: {product.name}</p>
             <p style={{ margin: 0, color: 'var(--text-secondary)' }}>₹{currentPrice}</p>
           </div>
           
           <div style={{ fontSize: '24px', color: 'var(--text-muted)' }}>+</div>

           <div style={{ flex: 1, minWidth: '200px' }}>
             {/* Simple logic to just pick a different item statically for the mock bundle */}
             {(() => {
                const bundleItem = products.find(p => p.id !== product.id) || products[0];
                return (
                  <>
                    <img src={bundleItem.imageUrl} alt={bundleItem.name} style={{ width: '100px', height: '100px', objectFit: 'contain' }}/>
                    <p style={{ fontWeight: 'bold', margin: '8px 0 0 0' }}>{bundleItem.name}</p>
                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>₹{bundleItem.price}</p>
                  </>
                );
             })()}
           </div>

           <div style={{ flex: 1, minWidth: '200px', textAlign: 'center' }}>
             <p style={{ marginBottom: '16px', fontWeight: 'bold' }}>Total Bundle Price: <span style={{ color: '#d00000', fontSize: '20px' }}>₹{currentPrice + (products.find(p => p.id !== product.id)?.price || 0)}</span></p>
             <button className="btn-primary" onClick={() => {
                 handleAddToCart();
                 const bItem = products.find(p => p.id !== product.id);
                 if (bItem) {
                    addToCart({ id: bItem.id, name: bItem.name, price: bItem.price.toString(), quantity: 1 });
                 }
                 alert("Bundle added successfully!");
             }}>Buy Both to Cart</button>
           </div>
        </div>
      </div>
      
      {/* Product Specific Customer Reviews */}
      <div style={{ gridColumn: '1 / -1', marginTop: '28px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
        <h2>Customer Reviews & Ratings</h2>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{ fontSize: '28px', fontWeight: 'bold', marginRight: '16px' }}>{product.rating} / 5</span>
          <span style={{ color: 'var(--text-muted)' }}>Based on {product.reviewsCount} verified purchases</span>
        </div>

        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
           {[
             { name: "Rahul S.", text: `Extremely satisfied with the quality of "${product.name}". Expected it to be good but it blew me away!`, stars: 5, date: "2 weeks ago" },
             { name: "Priya V.", text: `Packaging was excellent. The ${product.category.toLowerCase()} items arrived safely. Will definitely buy again.`, stars: 5, date: "1 month ago" },
             { name: "Amit K.", text: `Decent product. Meets the expectations and works as described.`, stars: 4, date: "3 months ago" }
           ].map((rev, i) => (
             <div key={i} className="card">
               <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <strong style={{ color: 'var(--link)' }}>{rev.name}</strong>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{rev.date}</span>
               </div>
               <div style={{ color: '#d4af37', marginBottom: '8px' }}>
                  {"★".repeat(rev.stars)}{"☆".repeat(5 - rev.stars)}
               </div>
               <p style={{ margin: 0, fontStyle: 'italic', fontSize: '14px' }}>"{rev.text}"</p>
             </div>
           ))}
        </div>
      </div>
    </div>
  );
}
